import React, { useState, useEffect, useRef } from "react";
import { SectionHeading, FieldLabel, Chip, StepInstructions } from "@/components/wizard/ui";
import { FieldInfo } from "@/components/wizard/InfoTip";
import { GOALS, ACTIVITY, TRAINING_SUPPORT } from "@/lib/planModel";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  weightForDisplay, weightToMetric, weightUnit,
  cmToFtIn, ftInToCm, kgToStone, stoneToKg,
} from "@/lib/units";
import {
  CalendarDays, User, Target, Salad, ChevronDown, Calendar as CalendarIcon,
} from "lucide-react";

const inputCls = "h-11 rounded-none bg-[#0F0F0F] border-white/10 focus-visible:ring-[#E2E30A] focus-visible:border-[#E2E30A]/60 text-white";
// Stop mouse-wheel from silently changing a focused number field while scrolling the page.
const noWheel = (e) => e.currentTarget.blur();

function UnitToggle({ testId, value, options, onChange }) {
  return (
    <div className="flex border border-white/15" data-testid={testId}>
      {options.map(([v, label]) => (
        <button key={v} type="button" data-testid={`${testId}-${v}`} onClick={() => onChange(v)}
          className={`px-2.5 py-1 text-[10px] uppercase tracking-wider font-semibold transition-colors ${
            value === v ? "bg-[#E2E30A] text-black" : "bg-transparent text-zinc-200 hover:text-white hover:bg-white/5"}`}>
          {label}
        </button>
      ))}
    </div>
  );
}

function Card({ index, title, Icon, accent = "#E2E30A", children, collapsible = false, defaultOpen = true, hint }) {
  const [open, setOpen] = useState(defaultOpen);
  // Sync when defaultOpen flips — this fires when `newPlan()` prefills the
  // profile after mount and the parent recomputes `isNewPlan`. Without this
  // the card would stay open forever because useState only reads its initial
  // value once.
  useEffect(() => { setOpen(defaultOpen); }, [defaultOpen]);
  return (
    <section
      className="border border-white/10 bg-[#141414] relative"
      style={{ boxShadow: `inset 3px 0 0 0 ${accent}` }}
    >
      <header
        className={`flex items-center gap-3 px-5 pt-4 pb-3 border-b border-white/5 ${collapsible ? "cursor-pointer select-none hover:bg-white/[0.02]" : ""}`}
        onClick={collapsible ? () => setOpen((o) => !o) : undefined}
        role={collapsible ? "button" : undefined}
        aria-expanded={collapsible ? open : undefined}
        data-testid={collapsible ? `step1-card-toggle-${index}` : undefined}
      >
        <div
          className="h-7 min-w-[28px] px-1.5 grid place-items-center border font-display text-xs font-bold"
          style={{ color: accent, borderColor: accent }}
        >
          {index}
        </div>
        <Icon className="h-4 w-4" style={{ color: accent }} />
        <h3 className="font-display uppercase tracking-[0.15em] text-sm text-white font-bold flex-1 min-w-0 truncate">{title}</h3>
        {collapsible && hint && !open && (
          <span className="text-[10px] uppercase tracking-wider text-zinc-400 hidden sm:inline truncate">{hint}</span>
        )}
        {collapsible && (
          <ChevronDown className={`h-4 w-4 text-zinc-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
        )}
      </header>
      {(!collapsible || open) && <div className="p-5">{children}</div>}
    </section>
  );
}

function nameFromDate(weekStart) {
  if (!weekStart) return "";
  const d = new Date(`${weekStart}T00:00:00`);
  if (isNaN(d.getTime())) return "";
  return `w/c ${d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`;
}

// The plan is authored Monday→Sunday, so the week must start on a Monday.
// Snap whatever date the athlete picks back to that week's Monday.
function mondayOf(dateStr) {
  if (!dateStr) return dateStr;
  const d = new Date(`${dateStr}T00:00:00`);
  if (isNaN(d.getTime())) return dateStr;
  const off = (d.getDay() + 6) % 7; // days since Monday
  d.setDate(d.getDate() - off);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export default function Step1Profile({ plan, patch, setTop, showErrors, isNewWeekSession }) {
  const { profile, goal, weekStart, name } = plan;
  const { user, refreshUser } = useAuth();
  // Debounced "save biometrics to the user account" — DOB/height/sex are
  // stored on the user so every future new-week plan can prefill them, even
  // if the athlete's most-recent plan is partial or gets wiped. Called
  // silently on every Step 1 edit; failures are swallowed so the wizard
  // never blocks on a background sync.
  const saveDefaultsRef = useRef(null);
  const pushDefaults = (patch) => {
    if (saveDefaultsRef.current) clearTimeout(saveDefaultsRef.current);
    saveDefaultsRef.current = setTimeout(async () => {
      try {
        await api.post("/users/profile-defaults", patch);
        if (refreshUser) refreshUser();
      } catch { /* best-effort — never blocks the wizard */ }
    }, 800);
  };
  const weightMode = profile.weightUnit === "lb" ? "lb" : profile.weightUnit === "st" ? "st" : "kg";
  const wSys = weightMode === "lb" ? "imperial" : "metric";
  const heightImperial = profile.heightUnit === "ftin";
  const errBorder = "border-[#FF3B30] focus-visible:ring-[#FF3B30]";
  const missAge = showErrors && !profile.age && !profile.dob;
  const todayIso = new Date().toISOString().slice(0, 10);
  // Lookup-label helpers so the collapsed card headers can show human labels
  // ("Controlled Fat Loss") instead of raw values ("fat-loss").
  const goalLabel = GOALS.find((g) => g.value === plan.goal)?.label;
  const activityLabel = ACTIVITY.find((a) => a.value === profile.activity)?.label;
  const supportLabel = TRAINING_SUPPORT.find((t) => t.value === profile.trainingSupport)?.label;
  // Format the athlete's weight for the collapsed Biometrics hint. Uses
  // whichever unit the athlete picked (kg/lb/st) — falls back to the numeric
  // metric weight when a unit label isn't available.
  const weightHint = (() => {
    if (!profile.weight) return null;
    if (profile.weightUnit === "st") {
      const { st, lb } = kgToStone(profile.weight);
      return `${st}st ${lb}lb`;
    }
    const v = weightForDisplay(profile.weight, wSys);
    const u = weightUnit(wSys);
    return v ? `${v}${u}` : null;
  })();
  const heightHint = (() => {
    if (!profile.height) return null;
    if (heightImperial) {
      const { ft, inch } = cmToFtIn(profile.height);
      return `${ft}′${inch}″`;
    }
    return `${profile.height}cm`;
  })();
  const [showAgeOverride, setShowAgeOverride] = useState(!!(profile.age && !profile.dob));

  // Compute age from DOB so it auto-updates as time passes.
  const setDob = (iso) => {
    if (!iso) {
      patch("profile", { dob: "" });
      pushDefaults({ dob: "" });
      return;
    }
    const d = new Date(iso);
    if (isNaN(d)) return;
    const now = new Date();
    let a = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a -= 1;
    patch("profile", { dob: iso, age: a >= 0 && a < 120 ? String(a) : profile.age });
    pushDefaults({ dob: iso });
  };
  // Wrappers that also mirror to the user account so DOB/height/sex are
  // remembered even for athletes who have only one (partial) saved plan.
  const setHeight = (cm) => {
    patch("profile", { height: cm });
    if (cm && !isNaN(Number(cm))) pushDefaults({ height: Number(cm) });
  };
  const setSex = (v) => {
    patch("profile", { sex: v });
    pushDefaults({ sex: v });
  };
  const missWeight = showErrors && !profile.weight;
  const missHeight = showErrors && !profile.height;
  // Only collapse Biometrics + Goal & Fuelling when the athlete explicitly
  // arrived via Home's "Start a new week" flow AND at least one biometric
  // was prefilled (either from a prior plan or from the Tracker's latest
  // weigh-in). "Continue editing" and "Pick a saved week" always show every
  // card fully expanded so the athlete can review/edit.
  const isNewPlan = !!isNewWeekSession
    && (!!(profile.age || profile.dob) || !!profile.weight);
  const initFtIn = profile.height ? cmToFtIn(profile.height) : { ft: "", inch: "" };
  const [ftStr, setFtStr] = useState(() => (initFtIn.ft === "" ? "" : String(initFtIn.ft)));
  const [inStr, setInStr] = useState(() => (initFtIn.inch === "" ? "" : String(initFtIn.inch)));
  const lastHUnit = useRef(profile.heightUnit);
  const lastHeight = useRef(profile.height);

  // Keep the raw typed weight locally so pounds entry isn't garbled by kg round-tripping.
  const [weightStr, setWeightStr] = useState(() => weightForDisplay(profile.weight, wSys));
  const lastUnit = useRef(profile.weightUnit);

  // Auto-fill the plan name if it's empty or still a legacy default. Runs
  // whenever weekStart changes so the field never lingers on "Untitled week"
  // or "My Weekly Plan" — athletes see the sensible week-derived name and
  // can override anytime.
  useEffect(() => {
    const current = (plan.name || "").trim();
    const derived = nameFromDate(weekStart);
    if (!derived) return;
    const isStale = !current || current === "Untitled week" || current === "My Weekly Plan";
    if (isStale && current !== derived) setTop("name", derived);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart]);
  const lastWeight = useRef(profile.weight);
  useEffect(() => {
    if (lastUnit.current !== profile.weightUnit) {
      setWeightStr(weightForDisplay(profile.weight, wSys));
    } else if (profile.weight !== lastWeight.current) {
      if (weightToMetric(weightStr, wSys) !== profile.weight) {
        setWeightStr(weightForDisplay(profile.weight, wSys));
      }
    }
    lastUnit.current = profile.weightUnit;
    lastWeight.current = profile.weight;
  }, [profile.weightUnit, wSys, profile.weight, weightStr]);

  const onWeightChange = (e) => {
    setWeightStr(e.target.value);
    patch("profile", { weight: weightToMetric(e.target.value, wSys) });
  };

  // Stone + pounds (UK). Kept as local strings; canonical weight stays in kg.
  const initSt = profile.weight ? kgToStone(profile.weight) : { st: "", lb: "" };
  const [stStr, setStStr] = useState(() => (initSt.st === "" ? "" : String(initSt.st)));
  const [stLbStr, setStLbStr] = useState(() => (initSt.lb === "" ? "" : String(initSt.lb)));
  useEffect(() => {
    if (profile.weightUnit !== "st") return;
    if (stoneToKg(stStr, stLbStr) !== profile.weight) {
      const d = profile.weight ? kgToStone(profile.weight) : { st: "", lb: "" };
      setStStr(d.st === "" ? "" : String(d.st));
      setStLbStr(d.lb === "" ? "" : String(d.lb));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.weightUnit, profile.weight]);
  const onStChange = (e) => { setStStr(e.target.value); patch("profile", { weight: stoneToKg(e.target.value, stLbStr) }); };
  const onStLbChange = (e) => { setStLbStr(e.target.value); patch("profile", { weight: stoneToKg(stStr, e.target.value) }); };

  // Keep the typed ft/in locally so imperial height entry isn't rounded by cm round-tripping.
  useEffect(() => {
    if (lastHUnit.current !== profile.heightUnit || profile.height !== lastHeight.current) {
      if (ftInToCm(ftStr, inStr) !== profile.height) {
        const d = profile.height ? cmToFtIn(profile.height) : { ft: "", inch: "" };
        setFtStr(d.ft === "" ? "" : String(d.ft));
        setInStr(d.inch === "" ? "" : String(d.inch));
      }
    }
    lastHUnit.current = profile.heightUnit;
    lastHeight.current = profile.height;
  }, [profile.heightUnit, profile.height, ftStr, inStr]);

  const onFtChange = (e) => {
    setFtStr(e.target.value);
    const cm = ftInToCm(e.target.value, inStr);
    patch("profile", { height: cm });
    if (cm) pushDefaults({ height: cm });
  };
  const onInChange = (e) => {
    setInStr(e.target.value);
    const cm = ftInToCm(ftStr, e.target.value);
    patch("profile", { height: cm });
    if (cm) pushDefaults({ height: cm });
  };

  return (
    <div>
      <SectionHeading
        eyebrow="Step 01"
        title="Athlete Profile"
        desc="Your baseline metrics — sex, age, weight, height, activity and goal — power the whole engine. Switch units anytime."
      />

      <StepInstructions>
        Set the week this plan runs from, review your biometrics and pick your baseline activity and primary goal. Your full name is set once at signup (edit it anytime from <b>Account Settings</b>). How you'll use the app, meal preferences and TrainingPeaks come next in <b>Step 2 · Setup</b>.
      </StepInstructions>

      {isNewPlan && (
        <div data-testid="step1-newweek-notice"
          className="mb-5 border border-[#4C9AFF]/40 bg-[#4C9AFF]/[0.08] px-4 py-3 text-xs text-zinc-200 leading-relaxed">
          <b className="text-[#4C9AFF]">New week?</b> Your <b>Biometrics</b> and <b>Goal & Fuelling</b> are pre-filled from
          your last plan — collapsed below since they rarely change week-to-week. Your <b>weight</b> auto-updates from
          the Tracker's daily weigh-ins. Expand either card if you need to tweak.
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 max-w-4xl min-w-0">

        {/* 01 · Plan Setup — the athlete's identity and the week these numbers belong to.
            Full name is set at signup and edited in Account Settings, so we don't repeat it here. */}
        <Card index="01" title="Plan Setup" Icon={CalendarDays} accent="#E2E30A"
          collapsible defaultOpen
          hint={[
            name || (weekStart ? `w/c ${weekStart}` : null),
            profile.name || null,
          ].filter(Boolean).join(" · ") || undefined}>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <FieldLabel info={
                <FieldInfo testId="info-weekstart" title="Week starting date">
                  Sets which Monday the plan runs from — auto-names the plan (e.g. "w/c 3 Jun 2026") and dates each day in your summary and exports.
                </FieldInfo>
              }>Week starting date</FieldLabel>
              <div className="relative">
                <Input data-testid="plan-weekstart-input" type="date" className={`${inputCls} date-dark pr-10`}
                  value={weekStart || ""}
                  onChange={(e) => {
                    const v = mondayOf(e.target.value);
                    setTop("weekStart", v);
                    // Only overwrite the plan name if the athlete hasn't given it
                    // a custom nickname (empty, still the default "w/c ..." or the
                    // legacy "My Weekly Plan"). This preserves custom names when
                    // the athlete edits the week start.
                    const current = (plan.name || "").trim();
                    const isDefault = !current || current === "My Weekly Plan" || current === "Untitled week" || /^w\/c\s+\d/i.test(current);
                    if (isDefault) {
                      const derived = nameFromDate(v);
                      if (derived) setTop("name", derived);
                    }
                  }} />
                <CalendarIcon aria-hidden className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white" />
              </div>
              <p data-testid="plan-name-preview" className="text-xs text-zinc-200 mt-2">
                Runs Mon–Sun{profile.name && <> · <span className="text-zinc-300">{profile.name}</span></>}
              </p>
            </div>
            <div>
              <FieldLabel info={
                <FieldInfo testId="info-plan-name" title="Plan nickname (optional)">
                  We default this to the week-commencing date so plans are easy to find. Override it here if you want a memorable nickname — e.g. "Ironman Taper" or "Race Week 3".
                </FieldInfo>
              }>Plan name</FieldLabel>
              <div className="relative">
                <Input data-testid="plan-name-input" className={`${inputCls} pr-24`}
                  value={name || ""} onChange={(e) => setTop("name", e.target.value)}
                  placeholder={nameFromDate(weekStart) || "w/c 3 Jun 2026"} />
                {nameFromDate(weekStart) && (name || "").trim() !== nameFromDate(weekStart) && (
                  <button type="button" data-testid="plan-name-reset"
                    onClick={() => setTop("name", nameFromDate(weekStart))}
                    title={`Use "${nameFromDate(weekStart)}"`}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-wider font-bold text-black bg-[#E2E30A] hover:bg-[#c9ca09] px-2 py-1 transition-colors">
                    Use w/c
                  </button>
                )}
              </div>
              <p className="text-[11px] text-zinc-300 mt-1.5">Defaults to <span className="text-[#E2E30A]">{nameFromDate(weekStart) || "the week you pick"}</span> — edit anytime.</p>
            </div>
          </div>
        </Card>

        {/* 02 · Bio — collapsed on new weeks (values carried over from the last plan; weight auto-syncs from Tracker weigh-ins) */}
        <Card index="02" title="Biometrics" Icon={User} accent="#00E5FF"
          collapsible defaultOpen={!isNewPlan}
          hint={[
            profile.sex ? profile.sex[0].toUpperCase() + profile.sex.slice(1) : null,
            profile.age ? `${profile.age}y` : null,
            weightHint,
            heightHint,
            activityLabel,
          ].filter(Boolean).join(" · ") || undefined}>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <FieldLabel info={
                <FieldInfo testId="info-sex" title="Sex (for BMR)">
                  Used in the Mifflin-St Jeor equation to estimate your resting energy. The male and female formulas differ by ~166 kcal — pick the one matching your physiology for the most accurate baseline.
                </FieldInfo>
              }>Sex (for BMR calc)</FieldLabel>
              <div className="flex flex-wrap gap-2">
                {[["male", "Male"], ["female", "Female"], ["other", "Other"]].map(([v, l]) => (
                  <Chip key={v} testId={`profile-sex-${v}`} active={profile.sex === v}
                    onClick={() => setSex(v)}>{l}</Chip>
                ))}
              </div>
            </div>

            <div className="md:col-span-2">
              <FieldLabel>Date of birth</FieldLabel>
              <div className="relative">
                <Input data-testid="profile-dob-input" type="date" max={todayIso}
                  className={`${inputCls} date-dark pr-10${missAge ? " " + errBorder : ""}`}
                  value={profile.dob || ""} onChange={(e) => setDob(e.target.value)} />
                <CalendarIcon aria-hidden className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white" />
              </div>
              <div className="flex items-center justify-between gap-2 mt-1.5">
                <p className="text-[11px] text-zinc-300">
                  {profile.dob && profile.age ? `Age ${profile.age} — auto-updates each birthday.` : "Age auto-calculates from your DOB."}
                </p>
                <button
                  type="button"
                  data-testid="toggle-age-override"
                  onClick={() => setShowAgeOverride((v) => !v)}
                  className="text-[10px] uppercase tracking-wider text-[#E2E30A] hover:text-white flex items-center gap-1"
                >
                  {showAgeOverride ? "Hide" : "Enter age manually"}
                  <ChevronDown className={`h-3 w-3 transition-transform ${showAgeOverride ? "rotate-180" : ""}`} />
                </button>
              </div>
              {missAge && !profile.age && <p className="text-[10px] text-[#FF3B30] mt-1">Required — enter your DOB (or an age below)</p>}
              {showAgeOverride && (
                <div className="mt-3">
                  <FieldLabel>Age (manual override)</FieldLabel>
                  <Input data-testid="profile-age-input" type="number" min="0" max="120" onWheel={noWheel} className={inputCls}
                    value={profile.age} onChange={(e) => patch("profile", { age: e.target.value, dob: "" })}
                    placeholder={profile.dob ? "auto from DOB" : "years"} />
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <FieldLabel>Body weight</FieldLabel>
                <UnitToggle testId="weight-unit" value={weightMode}
                  options={[["kg", "kg"], ["lb", "lb"], ["st", "st"]]}
                  onChange={(u) => patch("profile", { weightUnit: u, unitSystem: u === "kg" ? "metric" : "imperial" })} />
              </div>
              {weightMode === "st" ? (
                <div className="flex gap-2">
                  <Input data-testid="profile-weight-st-input" type="number" min="0" onWheel={noWheel} className={`${inputCls}${missWeight ? " " + errBorder : ""}`}
                    value={stStr} onChange={onStChange} placeholder="st" />
                  <Input data-testid="profile-weight-stlb-input" type="number" min="0" max="13.9" step="0.1" onWheel={noWheel} className={`${inputCls}${missWeight ? " " + errBorder : ""}`}
                    value={stLbStr} onChange={onStLbChange} placeholder="lb" />
                </div>
              ) : (
                <Input data-testid="profile-weight-input" type="number" min="0" onWheel={noWheel} className={`${inputCls}${missWeight ? " " + errBorder : ""}`}
                  value={weightStr}
                  onChange={onWeightChange}
                  placeholder={weightUnit(wSys)} />
              )}
              {missWeight && <p className="text-[10px] text-[#FF3B30] mt-1">Required</p>}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <FieldLabel>Height</FieldLabel>
                <UnitToggle testId="height-unit" value={heightImperial ? "ftin" : "cm"}
                  options={[["cm", "cm"], ["ftin", "ft-in"]]}
                  onChange={(u) => patch("profile", { heightUnit: u })} />
              </div>
              {heightImperial ? (
                <div className="flex gap-2">
                  <Input data-testid="profile-height-ft-input" type="number" min="0" onWheel={noWheel} className={`${inputCls}${missHeight ? " " + errBorder : ""}`}
                    value={ftStr} onChange={onFtChange} placeholder="ft" />
                  <Input data-testid="profile-height-in-input" type="number" min="0" max="11.9" step="0.1" onWheel={noWheel} className={`${inputCls}${missHeight ? " " + errBorder : ""}`}
                    value={inStr} onChange={onInChange} placeholder="in" />
                </div>
              ) : (
                <Input data-testid="profile-height-cm-input" type="number" min="0" onWheel={noWheel} className={`${inputCls}${missHeight ? " " + errBorder : ""}`}
                  value={profile.height} onChange={(e) => setHeight(e.target.value)}
                  placeholder="cm" />
              )}
              {missHeight && <p className="text-[10px] text-[#FF3B30] mt-1">Required</p>}
            </div>

            <div className="md:col-span-2">
              <FieldLabel info={
                <FieldInfo testId="info-activity" title="Baseline activity">
                  Non-training movement — desk work vs. tradesperson. Sets the multiplier applied to your BMR before training energy is added.
                </FieldInfo>
              }>Baseline activity (outside training)</FieldLabel>
              <div className="flex flex-wrap gap-2">
                {ACTIVITY.map((a) => (
                  <Chip key={a.value} testId={`profile-activity-${a.value}`} active={profile.activity === a.value}
                    onClick={() => patch("profile", { activity: a.value })}>{a.label}</Chip>
                ))}
              </div>
            </div>
          </div>
        </Card>

        {/* 03 · Performance goals — collapsed on new weeks (goals rarely shift week-to-week) */}
        <Card index="03" title="Goal & Fuelling" Icon={Target} accent="#FF6A3D"
          collapsible defaultOpen={!isNewPlan}
          hint={[goalLabel, supportLabel ? `${supportLabel} fuelling` : null].filter(Boolean).join(" · ") || undefined}>
          <div className="grid gap-5">
            <div>
              <FieldLabel info={
                <FieldInfo testId="info-goal" title="Primary goal">
                  <div><b className="text-white">Performance</b> — fuel to train hard and adapt; no deliberate deficit.</div>
                  <div><b className="text-white">Maintenance</b> — hold your weight while fully covering training.</div>
                  <div><b className="text-white">Controlled Fat Loss</b> — a modest deficit applied mainly to rest and easy days, protecting your key sessions.</div>
                  <div><b className="text-white">Recovery</b> — extra protein and fat protection to support return to training.</div>
                </FieldInfo>
              }>Primary goal</FieldLabel>
              <div className="flex flex-wrap gap-2">
                {GOALS.map((g) => (
                  <Chip key={g.value} testId={`profile-goal-${g.value}`} active={goal === g.value}
                    onClick={() => setTop("goal", g.value)}>{g.label}</Chip>
                ))}
              </div>
            </div>

            <div>
              <FieldLabel info={
                <FieldInfo testId="info-support" title="Training-energy support">
                  How much of each session's estimated energy is added back to that day's target — and how readily in-session gels/carb drinks appear in your plan.
                  <div><b className="text-white">Conservative</b> — adds less back; gels only on the longest days.</div>
                  <div><b className="text-white">Balanced</b> — fuels most training without replacing every calorie; gels from ~75 min (recommended).</div>
                  <div><b className="text-white">Full Fuelling</b> — replaces the most; gels appear sooner (~60 min).</div>
                </FieldInfo>
              }>Training-energy support</FieldLabel>
              <div className="flex flex-wrap gap-2">
                {TRAINING_SUPPORT.map((t) => (
                  <Chip key={t.value} testId={`profile-support-${t.value}`} active={profile.trainingSupport === t.value}
                    onClick={() => patch("profile", { trainingSupport: t.value })}>{t.label}</Chip>
                ))}
              </div>
              <p className="text-xs text-zinc-200 mt-2 leading-relaxed">
                Controls how much session energy is added back <span className="text-[#E2E30A]">and</span> how aggressively in-session gels/carb drinks are prescribed — nudge it to match your gut and preference.
              </p>
            </div>
          </div>
        </Card>

        {/* Nutrition Preferences moved to Step 2 · Dietary — only relevant
            when meal planning is on. */}

      </div>
    </div>
  );
}
